import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CYCLE_YEAR } from '../../config/recruitingCycle';
import { useAuth } from '../../contexts/AuthContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import BouncingImage from '../BouncingImage';

const QuestBanner = () => {
  const navigate = useNavigate();
  const { isAuthenticated, checkAuth } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingIn, setIsFadingIn] = useState(false);
  const { userProfile, setUserProfile, isAchievementModalVisible } = useContext(GlobalContext);

  useEffect(() => {
    // First set visible after a delay
    if (userProfile && !isAchievementModalVisible) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        // Then trigger fade in after another small delay
        setTimeout(() => setIsFadingIn(true), 100);
      }, 1000); // Wait 1 second before showing
      return () => clearTimeout(timer);
    }
  }, [userProfile, isAchievementModalVisible]);

  const updateNotificationsSeen = async () => {
    try {
      const userID = userProfile.user_id;

      await axios.put(`/api/v1/user/notifications-seen/${userID}`, {
        notification_name: 'quest_banner',
      });

      // Update local state
      setUserProfile((prev) => ({
        ...prev,
        notifications_seen: [...(prev.notifications_seen || []), 'quest_banner'],
      }));
    } catch (error) {
      console.error('Error updating notifications:', error);
    }
  };

  const handleClose = () => {
    updateNotificationsSeen();
    setIsVisible(false);
  };

  const handleYes = () => {
    updateNotificationsSeen();
    navigate('/accept-quest');
  };

  if (!isVisible || !userProfile) return null;

  return (
    <div className="fixed inset-0 z-9999 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-6">
        {/* Backdrop */}
        <div
          className={`fixed inset-0 transition-opacity duration-500 ${isFadingIn ? 'bg-black/50' : 'bg-black/0'}`}
          onClick={handleClose}
        ></div>

        {/* Modal */}
        <div
          className={`relative max-w-xl transition-all duration-500 transform ${isFadingIn ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
        >
          <div className="bg-linear-to-r from-purple-600 to-blue-400 animate-pulse-gradient relative rounded-xl shadow-2xl">
            <div className="absolute inset-0 bg-linear-to-r from-pink-500 to-indigo-500 animate-pulse-gradient-reverse opacity-70 rounded-xl"></div>
            <div className="px-6 py-8 relative z-5">
              <div className="flex items-center gap-6">
                <div className="shrink-0">
                  <BouncingImage width={8} imageLink="/badges/kitty_idea.png" />
                </div>
                <div className="text-center grow">
                  <h2 className="text-2xl font-bold text-white text-shadow-sm mb-8">
                    {userProfile.first_name}! Are you looking for a {CYCLE_YEAR} summer internship?
                  </h2>
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={handleYes}
                      className="py-3 px-8 inline-flex items-center gap-x-2 text-lg font-medium rounded-lg border-2 border-white text-white hover:border-white/70 hover:text-white/70 focus:outline-hidden focus:border-white/70 focus:text-white/70 disabled:opacity-50 disabled:pointer-events-none transition-transform hover:scale-105"
                    >
                      Yes!
                    </button>
                    <button
                      onClick={handleClose}
                      className="py-3 px-8 inline-flex items-center gap-x-2 text-lg font-medium rounded-lg border-2 border-white/30 text-white/70 hover:border-white/50 hover:text-white/50 focus:outline-hidden focus:border-white/50 focus:text-white/50 disabled:opacity-50 disabled:pointer-events-none transition-transform hover:scale-105"
                    >
                      Not now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes pulseGradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        @keyframes pulseGradientReverse {
          0% {
            background-position: 100% 50%;
          }
          50% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 100% 50%;
          }
        }
        .animate-pulse-gradient {
          background-size: 200% 200%;
          animation: pulseGradient 3s ease infinite;
        }
        .animate-pulse-gradient-reverse {
          background-size: 200% 200%;
          animation: pulseGradientReverse 3s ease infinite;
        }
        .text-shadow-sm {
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default QuestBanner;
